import { DateTime } from 'luxon';
import { ISO6391LanguageCode } from 'shared/types/locale';
import { DayMomentValidation, TimeSetter } from 'shared/types/time';

export enum DateFormats {
  'shortISOString' = 'yyyy-MM-dd',
  'isoWithTimezone' = `yyyy-MM-dd'T'HH:mm:ssXXXX`,
  'shortString' = 'dd/MM/yyyy',
}

/** PT-7000: unified issue date display (date-only fields). */
export const ISSUE_DATE_DISPLAY_FORMAT = 'dd.MM.yyyy';

/** PT-7000: unified issue date-time display (stepper, table datetime columns). */
export const ISSUE_DATETIME_DISPLAY_FORMAT = 'dd.MM.yyyy HH:mm';

export function shortISOStringToProjectTimezonedString(
  date: string,
  timezone: string,
  daymoment: DayMomentValidation | undefined
): string {
  const luxondate = DateTime.fromFormat(date, DateFormats.shortISOString, {
    zone: timezone,
  });

  if (!daymoment) {
    return localTimeInProjectTimezone(luxondate, timezone);
  }

  return timeSetter[daymoment](luxondate).toString();
}

export function shortISOStringToProjectTimezonedDate(
  date: string,
  timezone: string,
  daymoment: DayMomentValidation
): DateTime {
  return timeSetter[daymoment](
    DateTime.fromFormat(date, DateFormats.shortISOString, {
      zone: timezone,
    })
  );
}

export function isoWithTimezoneToMuiDatepicker(
  date: DateFormats.isoWithTimezone | undefined | null
): DateTime | null {
  return date ? DateTime.fromISO(date) : null;
}

export function setDayStart(date: DateTime): DateTime {
  return date.set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
}

function setDayEnd(date: DateTime): DateTime {
  return date
    .set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    })
    .plus({ days: 1 })
    .minus({ milliseconds: 1 });
}

const timeSetter: TimeSetter = {
  [DayMomentValidation.start]: setDayStart,
  [DayMomentValidation.end]: setDayEnd,
};

function localTimeInProjectTimezone(
  date: DateTime,
  timezone: string
): string {
  const now = DateTime.now().setZone(timezone);
  const selectedDateWithCurrentTime = now.set({
    year: date.year,
    month: date.month,
    day: date.day,
  });

  return selectedDateWithCurrentTime.toString();
}

function shortStringToProjectTimezonedString(
  date: string | null | undefined,
  timezone: string,
  daymoment: DayMomentValidation | undefined
): string | null {
  if (!date) {
    return null;
  }
  const luxondate = DateTime.fromFormat(date, 'dd/MM/yyyy', {
    zone: timezone,
  });

  if (!daymoment) {
    return localTimeInProjectTimezone(luxondate, timezone);
  }

  return timeSetter[daymoment](luxondate).toString();
}

export function isoToProjectTimezonedString(
  date: string,
  timezone: string,
  daymoment?: DayMomentValidation
): DateFormats.isoWithTimezone {
  const luxondate = isoToProjectTimezonedDate(date, timezone, daymoment);
  return (
    luxondate && (luxondate.toString() as DateFormats.isoWithTimezone)
  );
}

export function isoToProjectTimezonedDate(
  date: string,
  timezone: string,
  daymoment?: DayMomentValidation
): DateTime {
  const luxondate = DateTime.fromISO(date, {
    zone: timezone,
  });

  if (!daymoment) {
    return luxondate;
  }

  return timeSetter[daymoment](luxondate);
}
/*
Creates exactly the same datetime, but with stripped timezone information
Useful for Excel dates (https://github.com/exceljs/exceljs/issues/486)
Examples:
isoToUTCDate('2022-05-22T00:00:00.000+03:00', 'Europe/Istanbul') => '2022-05-22T00:00:00.000Z'
isoToUTCDate('2022-05-17T00:00:00.000+02:00', 'Europe/Warsaw) => '2022-05-17T00:00:00.000Z'
*/
export function isoToUTCDate(date: string, timezone: string): DateTime {
  const timezonedDate = isoToProjectTimezonedDate(date, timezone);

  return timezonedDate.plus({ minutes: timezonedDate.offset });
}

export function datepickerDateToProjectTimezonedString(
  date: Date | null | undefined,
  timezone: string,
  daymoment: DayMomentValidation | undefined
): string | null {
  if (!date) {
    return null;
  }

  // we ignore datepicker timezone and use the date as it would be in a project timezone
  const luxondate = DateTime.fromJSDate(date).setZone(timezone, {
    keepLocalTime: true,
  });

  if (!daymoment) {
    return localTimeInProjectTimezone(luxondate, timezone);
  }

  return timeSetter[daymoment](luxondate).toString();
}

export function dateTimeToDayMomentString(
  date: DateTime,
  daymoment: DayMomentValidation
): string | null {
  return timeSetter[daymoment](date).toString();
}

export function dateTimeToDayMomentDateTime(
  date: DateTime,
  daymoment: DayMomentValidation
): DateTime | null {
  return timeSetter[daymoment](date);
}

export function isValidDate(date: any): boolean {
  return DateTime.isDateTime(date);
}

export function isoToDatepickerDecievedDate(
  date: string | null | undefined,
  timezone: string
): string | null {
  if (!date) {
    return null;
  }

  const projectDate = DateTime.fromISO(date, {
    zone: timezone,
  });

  const now = DateTime.now().setZone(timezone);
  const localUnmovedDate = now.set({
    year: projectDate.year,
    month: projectDate.month,
    day: projectDate.day,
    hour: projectDate.hour,
    minute: projectDate.minute,
    second: projectDate.second,
    millisecond: projectDate.millisecond,
  });

  return localUnmovedDate.toString();
}

export function isoToMuiDatepickerDate(
  date: string | null | undefined,
  timezone: string
): DateTime | null {
  if (!date) {
    return null;
  }

  const timezonedDate = DateTime.fromISO(date, {
    zone: timezone,
  });

  return timezonedDate;
}

/** ISO string from Luxon DateTime or parseable ISO text; undefined when invalid. */
export function dateTimeToIsoString(
  value: DateTime | string | null | undefined
): string | undefined {
  if (value == null) {
    return undefined;
  }

  if (DateTime.isDateTime(value)) {
    return value.isValid ? value.toISO() ?? undefined : undefined;
  }

  if (typeof value === 'string' && value.length > 0) {
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed.toISO() ?? undefined : undefined;
  }

  return undefined;
}

export function toStandardDateDisplayStringFormat(
  date: string | DateTime,
  timezone: string
): string {
  if (typeof date === 'string') {
    date = DateTime.fromISO(date, {
      zone: timezone,
    });
  }

  return date.toLocaleString(DateTime.DATE_SHORT, {
    locale: 'en-gb',
  });
}

export function toIssueDateDisplayStringFormat(
  date: string | DateTime,
  timezone: string
): string {
  return toZonedDateTime(date, timezone).toFormat(ISSUE_DATE_DISPLAY_FORMAT);
}

export function toIssueDateTimeDisplayStringFormat(
  date: string | DateTime,
  timezone: string
): string {
  return toZonedDateTime(date, timezone).toFormat(ISSUE_DATETIME_DISPLAY_FORMAT);
}

export function toIssueDateDisplayStringFromIsoDateKey(
  isoDateKey: string,
  timezone: string
): string {
  return DateTime.fromFormat(isoDateKey, DateFormats.shortISOString, {
    zone: timezone,
  }).toFormat(ISSUE_DATE_DISPLAY_FORMAT);
}

export function toStandardTimeDisplayStringFormat(
  date: string | DateTime,
  timezone: string
): string {
  const dt =
    typeof date === 'string'
      ? DateTime.fromISO(date, { zone: timezone })
      : date;

  return dt.toFormat('HH:mm');
}

export function getIsoDateKeyInTimezone(
  isoDate: string,
  timezone: string
): string {
  return DateTime.fromISO(isoDate, { zone: timezone }).toFormat(
    DateFormats.shortISOString
  );
}

function toZonedDateTime(date: string | DateTime, timezone: string): DateTime {
  return typeof date === 'string'
    ? DateTime.fromISO(date, { zone: timezone })
    : date.setZone(timezone);
}

export function toLocalizedDateDisplayStringFormat(
  date: string | DateTime,
  timezone: string,
  locale: ISO6391LanguageCode
): string {
  return toZonedDateTime(date, timezone).setLocale(locale).toLocaleString({
    ...DateTime.DATE_FULL,
  });
}

/**
 * DataGrid helper: converts JS `Date` to localized date string (without time).
 * @param date - Date object to convert
 * @param timezone - Timezone string (e.g., 'Europe/Warsaw')
 * @param locale - ISO6391 language code (e.g., 'pl', 'en')
 * @returns Localized date string or null if date is null/undefined
 * @example
 * // For locale 'pl': "15 stycznia 2025"
 * // For locale 'en': "January 15, 2025"
 * displayDataGridDate(new Date('2025-01-15'), 'Europe/Warsaw', 'pl')
 */
export function displayDataGridDate(
  date: Date | null | undefined,
  timezone: string,
  locale: ISO6391LanguageCode
): string | null {
  if (!date) return null;

  const dateTime = DateTime.fromJSDate(date, { zone: timezone });

  return toLocalizedDateDisplayStringFormat(dateTime, timezone, locale);
}

/**
 * DataGrid helper: converts JS `Date` to localized date string (with time).
 * @param date - Date object to convert
 * @param timezone - Timezone string (e.g., 'Europe/Warsaw')
 * @param locale - ISO6391 language code (e.g., 'pl', 'en')
 * @returns Localized date-time string or null if date is null/undefined
 * @example
 * // For locale 'pl': "15 stycznia 2025, 13:45" (exact separator can vary by browser/locale)
 * // For locale 'en': "January 15, 2025, 01:45 PM" (exact format can vary by browser/locale)
 * displayDataGridDateWithTime(new Date('2025-01-15T13:45:00Z'), 'Europe/Warsaw', 'pl')
 */
export function displayDataGridDateWithTime(
  date: Date | null | undefined,
  timezone: string,
  locale: ISO6391LanguageCode
): string | null {
  if (!date) return null;

  const dateTime = DateTime.fromJSDate(date, { zone: timezone });

  return toLocalizedDateDisplayStringFormatWithTime(dateTime, timezone, locale);
}

export function toLocalizedDateDisplayStringFormatWithTime(
  date: string | DateTime,
  timezone: string,
  locale: ISO6391LanguageCode
): string {
  return toZonedDateTime(date, timezone).setLocale(locale).toLocaleString({
    ...DateTime.DATE_FULL,
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function toShortISO(date: DateTime): string {
  return date.toFormat(DateFormats.shortISOString);
}

export function isoStringToShortISO(
  isoString?: string | null
): string | undefined {
  return isoString?.slice(0, 10);
}

export function nowISO(): string {
  return DateTime.now().toISO();
}

export function nowTimezonedDateTime(
  timezone: string,
  daymoment?: DayMomentValidation
): DateTime {
  return daymoment
    ? timeSetter[daymoment](DateTime.now().setZone(timezone))
    : DateTime.now().setZone(timezone);
}

export function isoToLocalDate(string: string): Date {
  return new Date(DateTime.fromISO(string).valueOf());
}

export function onDayStart(date: DateTime): DateTime {
  return date.startOf('day');
}

export function onDayEnd(date: DateTime): DateTime {
  return date.endOf('day');
}

export function toDayStartInTimezone(
  date: Date,
  projectTimezone: string
): Date {
  return DateTime.fromJSDate(date, { zone: projectTimezone })
    .startOf('day')
    .toJSDate();
}

export function toDayEndInTimezone(
  date: Date,
  projectTimezone: string
): Date {
  return DateTime.fromJSDate(date, { zone: projectTimezone })
    .endOf('day')
    .toJSDate();
}

export enum DateFormatFromUnit {
  'day' = 'yyyy-MM-dd',
  'month' = 'yyyy-MM',
  'year' = 'yyyy',
}

export function anyToDateUtc(value: unknown): DateTime {
  if (value == null) {
    return DateTime.invalid('invalid input');
  }

  if (typeof value === 'string') {
    return DateTime.fromISO(value).toUTC();
  }

  if (typeof value === 'number') {
    return DateTime.fromMillis(value).toUTC();
  }

  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toUTC();
  }

  try {
    return DateTime.fromISO(String(value)).toUTC();
  } catch {
    return DateTime.invalid('invalid input');
  }
}

/**
 * Returns current date in ISO format (YYYY-MM-DD)
 * @param timezone - Optional timezone (default: local)
 * @returns Date string in format "2025-10-02"
 */
export function getISODateString(timezone?: string): string {
  const dt = timezone ? DateTime.now().setZone(timezone) : DateTime.now();
  return dt.toISODate() ?? '';
}

export function toUtcMillis(date: DateTime): number {
  return date.toUTC().toMillis();
}

export function startOfDayToUtcMillis(
  date: DateTime | null
): number | null {
  if (!date) return null;
  return toUtcMillis(onDayStart(date));
}

export function endOfDayToUtcMillis(date: DateTime | null): number | null {
  if (!date) return null;
  return toUtcMillis(onDayEnd(date));
}

/**
 * Converts timestamp in milliseconds to DateTime with specified timezone
 * @param millis - Timestamp in milliseconds (null/undefined returns null)
 * @param timezone - Timezone string (e.g., 'Europe/Warsaw')
 * @returns DateTime object or null if millis is null/undefined
 */
export function millisToDateTime(
  millis: number | null | undefined,
  timezone: string
): DateTime | null {
  if (millis == null) return null;
  return DateTime.fromMillis(millis, { zone: timezone });
}

/**
 * Converts DateTime to timezone with keepLocalTime option
 * @param date - DateTime object (null/undefined returns null)
 * @param timezone - Timezone string (e.g., 'Europe/Warsaw')
 * @returns DateTime object in specified timezone or null if date is null/undefined
 */
export function dateTimeToTimezone(
  date: DateTime | null | undefined,
  timezone: string
): DateTime | null {
  if (!date) return null;
  return date.setZone(timezone, { keepLocalTime: true });
}
